import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn, gridExportMatrix } from '../../components/AppDataGrid';
import { TrendChartPopup, TrendIconButton } from '../trend';
// Shared Field-Data column-config engine — the SAME Sample Field Configuration
// that drives Field Data Entry now drives this read-only view (user request
// 2026-09-02). Read-only, so columns pass through stripEditable.
import {
  type FieldConfigItem, type DynamicFieldSet, EMPTY_DYN,
  assembleTab, applyFieldConfig, mergeDynamic, stripEditable,
  STATION_FIELD_MAP, SAMPLING_FIELD_MAP, MONITORING_FIELD_MAP,
} from './fieldDataColumns';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading } from '../../components/loading';
import { AppToolbar } from '../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNavActions } from '../../hooks/useNavActions';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface ValidationListRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
}

interface StationRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  blended:        boolean;
  latitude:       string | null;
  longitude:      string | null;
  temperature:    string | null;
  humidity:       string | null;
  stationComment: string | null;
  [key: string]:  unknown; // dynamic (config-driven) extra fields
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string;
  testSummary:    string;
  enteredBy:      string;
  enteredDate:    string | null;
  [key: string]:  unknown; // dynamic (config-driven) extra fields
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string;
  unit:               string;
  monitoredBy:        string;
  monitoredDate:      string | null;
  enteredBy:          string;
  enteredDate:        string | null;
  [key: string]:      unknown; // dynamic (config-driven) extra fields
}

type TabKey = 'stations' | 'sampling' | 'monitoring';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataValidationPage() {
  // ── List state ──────────────────────────────────────────────────────────────
  const [listRows,    setListRows]    = useState<ValidationListRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listLoadMs,  setListLoadMs]  = useState<number | null>(null);
  const [listError,   setListError]   = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // Free-text search over the list (same behaviour as Field Data Entry).
  const [search,      setSearch]      = useState('');

  // ── Detail state ────────────────────────────────────────────────────────────
  const [view,          setView]          = useState<'list' | 'detail'>('list');
  const [selectedJob,   setSelectedJob]   = useState<ValidationListRow | null>(null);
  const [activeTab,     setActiveTab]     = useState<TabKey>('stations');
  const [visitedTabs,   setVisitedTabs]   = useState<Set<TabKey>>(new Set());
  const [stations,      setStations]      = useState<StationRow[]>([]);
  const [sampling,      setSampling]      = useState<SamplingRow[]>([]);
  const [monitoring,    setMonitoring]    = useState<MonitoringRow[]>([]);
  // Grid-visible (filtered + sorted) rows per grid — Export uses ONLY these so a
  // column-filtered grid exports just what's shown (user request 2026-08-31).
  const [listVisible,       setListVisible]       = useState<ValidationListRow[]>([]);
  const [stationVisible,    setStationVisible]    = useState<StationRow[]>([]);
  const [samplingVisible,   setSamplingVisible]   = useState<SamplingRow[]>([]);
  const [monitoringVisible, setMonitoringVisible] = useState<MonitoringRow[]>([]);

  // Row-selection checkboxes on all three review tabs — GoLive parity (the
  // FieldDataReview views carry the same leftmost tick column + select-all header).
  // Selection is a REVIEW AID only: Validate/Rollback still act on the whole job
  // (GoLive RollBack_Accepting), so nothing here narrows what those buttons do.
  const [selStationIds,    setSelStationIds]    = useState<Set<string | number>>(new Set());
  const [selSamplingIds,   setSelSamplingIds]   = useState<Set<string | number>>(new Set());
  const [selMonitoringIds, setSelMonitoringIds] = useState<Set<string | number>>(new Set());
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadMs,  setDetailLoadMs]  = useState<number | null>(null);
  // Permission gate ported from GoLive FieldDataReviewController.OnActivated —
  // Validate/Rollback are enabled only for admins or roles with Write=true on
  // the FieldDataReview1 navigation permission.
  const [canWrite, setCanWrite] = useState(true);

  // Module Access Control (Role-Based MAC) layered on top of the nav-permission
  // canWrite above (Sampling Allocation pattern, useNavActions). Falls open for
  // admins / no configured MAC level. Validate + Rollback are write actions.
  const perm = useNavActions('fielddata-validation');
  const canWriteEff = canWrite && perm.write;

  // Trend popup (Routing the Trend Icon, Part B) — the Monitoring "Trend" column.
  const [trendOpen,  setTrendOpen]  = useState(false);
  const [trendSite,  setTrendSite]  = useState('');
  const [trendParam, setTrendParam] = useState('');

  // Sample Field Configuration (per the job's VisualMatrix) — drives the tab
  // grid columns exactly like Field Data Entry (reuses the field-data-entry
  // field-config + dynamic-fields endpoints; same job → same matrix).
  const [fieldConfig,  setFieldConfig]  = useState<FieldConfigItem[]>([]);
  const [stationDyn,   setStationDyn]   = useState<DynamicFieldSet>(EMPTY_DYN);
  const [samplingDyn,  setSamplingDyn]  = useState<DynamicFieldSet>(EMPTY_DYN);
  const [monitoringDyn, setMonitoringDyn] = useState<DynamicFieldSet>(EMPTY_DYN);

  const notify                              = useNotify();
  const { confirm: confirmDlg, prompt: promptDlg } = useConfirm();
  const detailT0 = useRef<number>(0);

  // ── Load list ──────────────────────────────────────────────────────────────
  const loadList = useCallback(async (searchText?: string) => {
    setListLoading(true);
    setListError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (searchText) params.search = searchText;
      const res = await api.get('/api/v1/field-data-validation', { params });
      setListRows((res.data as { data: ValidationListRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setListLoadMs(performance.now() - t0);
      setListLoading(false);
    }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);

  // ── Single-select (radio-button style) — only one job may be ticked at a
  // time, since Load/Rollback operate on one record here. Header "select all"
  // collapses to the last row rather than checking every row.
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const newlyAdded = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(new Set([newlyAdded ?? [...next][next.size - 1]!]));
  }, [selectedIds]);

  // ── Permission gate (ported from GoLive FieldDataReviewController.OnActivated) ──
  useEffect(() => {
    api.get('/api/v1/field-data-validation/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Load detail (after clicking Load) ──────────────────────────────────────
  const openDetail = useCallback(async (job: ValidationListRow) => {
    setSelectedJob(job);
    setDetailLoading(true);
    setActiveTab('stations');
    setVisitedTabs(new Set(['stations']));
    // Clear per-tab ticks so one job's selection never carries into the next.
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    detailT0.current = performance.now();
    // reset any previous job's config so its columns never leak into this one
    setFieldConfig([]); setStationDyn(EMPTY_DYN); setSamplingDyn(EMPTY_DYN); setMonitoringDyn(EMPTY_DYN);
    let stationRows: StationRow[] = [];
    let samplingRows: SamplingRow[] = [];
    let monitoringRows: MonitoringRow[] = [];
    try {
      const [st, sp, mn] = await Promise.all([
        api.get(`/api/v1/field-data-validation/${job.jobOid}/stations`),
        api.get(`/api/v1/field-data-validation/${job.jobOid}/sampling`),
        api.get(`/api/v1/field-data-validation/${job.jobOid}/monitoring`),
      ]);
      stationRows    = (st.data as { data: StationRow[] }).data ?? [];
      samplingRows   = (sp.data as { data: SamplingRow[] }).data ?? [];
      monitoringRows = (mn.data as { data: MonitoringRow[] }).data ?? [];
      setStations(stationRows);
      setSampling(samplingRows);
      setMonitoring(monitoringRows);
      setDetailLoadMs(performance.now() - detailT0.current);
      setView('detail');
    } catch {
      notify.error('Failed to load job detail. Please try again.');
      setDetailLoading(false);
      return;
    } finally {
      setDetailLoading(false);
    }

    // Sample Field Configuration (same job → same VisualMatrix as Field Data
    // Entry, so we reuse Entry's field-config + dynamic-fields endpoints).
    // Non-blocking: the base grid already shows; config refines the columns.
    try {
      const base = `/api/v1/field-data-entry/${job.jobOid}`;
      const [fc, dSt, dSp, dMn] = await Promise.all([
        api.get(`${base}/field-config`),
        api.get(`${base}/dynamic-fields?fieldClass=1`),
        api.get(`${base}/dynamic-fields?fieldClass=0`),
        api.get(`${base}/dynamic-fields?fieldClass=2`),
      ]);
      const stDyn = (dSt.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      const spDyn = (dSp.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      const mnDyn = (dMn.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      setFieldConfig((fc.data as { data: FieldConfigItem[] }).data ?? []);
      setStationDyn(stDyn); setSamplingDyn(spDyn); setMonitoringDyn(mnDyn);
      setStations(mergeDynamic(stationRows, stDyn, r => r.sampleLoginOid));
      setSampling(mergeDynamic(samplingRows, spDyn, r => r.sampleLoginOid));
      setMonitoring(mergeDynamic(monitoringRows, mnDyn, r => r.sampleParameterOid));
    } catch {
      /* non-fatal — the default (static) columns already render */
    }
  }, [notify]);

  const closeDetail = useCallback(() => {
    setView('list');
    setSelectedJob(null);
    setStations([]); setSampling([]); setMonitoring([]);
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    setVisitedTabs(new Set());
    void loadList();
  }, [loadList]);

  // ── Tab navigation — marks tab as visited ───────────────────────────────────
  const switchTab = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    setVisitedTabs(prev => new Set([...prev, tab]));
  }, []);

  const allTabsVisited = visitedTabs.has('stations') && visitedTabs.has('sampling') && visitedTabs.has('monitoring');

  // ── Validate ────────────────────────────────────────────────────────────────
  // Validates ONLY the samples TICKED in the Sampling Information tab, matching
  // GoLive FieldDataReviewController.FieldDataValidated_Execute: it gates on
  //   ((ListView)FindItem("SamplingInformation").InnerView).SelectedObjects.Count > 0
  // ("Select the sampleID to validate" otherwise) and loops
  // `foreach (SampleLogIn objSampling in ...SelectedObjects)`.
  // FIXED 2026-08-03 alongside Rollback (same defect, same form): this posted an
  // EMPTY body, so the server fell back to "all PendingValidation rows for the job"
  // and validated everything regardless of the ticks. The API already accepted
  // selectedSampleOids — only the frontend was omitting them.
  const handleValidate = useCallback(async () => {
    if (!selectedJob) return;
    // NOTE: there is deliberately NO "all three tabs visited" gate (removed
    // 2026-08-03). GoLive FieldDataValidated_Execute gates ONLY on the Sampling
    // selection + the FieldDataReview1 Write permission — it never requires the
    // reviewer to open every tab. The old rule blocked users who legitimately HAD
    // Write (verified: C16294 / lisa@strikewater.com resolves Write=1 on
    // FieldDataReview1), which read as a broken permission. The tab ticks remain as
    // PROGRESS indicators in the stepper/hint, not as a gate.
    if (!canWriteEff) {
      notify.error('You do not have permission to validate field data.');
      return;
    }
    // GoLive gate — validation is per-sample and needs an explicit pick.
    const picked = sampling
      .filter(r => selSamplingIds.has(r.sampleLoginOid))
      .map(r => r.sampleLoginOid);
    if (picked.length === 0) {
      notify.error('Select the sampleID to validate.');
      setActiveTab('sampling');
      return;
    }
    const ok = await confirmDlg({
      message: `Do you want to Validate ${picked.length} selected sample${picked.length > 1 ? 's' : ''} in job ${selectedJob.jobId}?`,
      tone: 'default',
    });
    if (!ok) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-validation/${selectedJob.jobOid}/validate`,
          { selectedSampleOids: picked }),
        { blocking: true, label: 'Validating…' }
      );
      notify.success('Validated successfully.');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Validate failed.';
      notify.error(msg);
    }
  }, [selectedJob, allTabsVisited, canWriteEff, sampling, selSamplingIds, confirmDlg, notify, closeDetail]);

  // ── Rollback (detail view) — rolls back ONLY the samples TICKED in the
  // Sampling Information tab, matching GoLive
  // FieldDataReviewController.FieldDataRollBack_Execute + RollBack_Accepting:
  // the detail-view branch gates on
  //   ((ListView)FindItem("SamplingInformation").InnerView).SelectedObjects.Count > 0
  // ("Select atleast one Sample to rollback" otherwise) and then loops
  // `foreach (SampleLogIn objSampling in lvSamplingInfo.SelectedObjects)`.
  // FIXED 2026-08-03 (user report): this previously sent EVERY row in the tab
  // (`sampling.map(...)`), so ticking 2 samples rolled back the whole job. The
  // API + AI_FieldDataRollback_SP already honoured @SelectedSampleOidsJson — only
  // the frontend was passing the wrong set.
  const handleRollback = useCallback(async () => {
    if (!selectedJob) return;
    if (!canWriteEff) { notify.error('You do not have permission to roll back field data.'); return; }
    // GoLive gate — a rollback is per-sample and needs an explicit pick.
    if (selSamplingIds.size === 0) {
      notify.error('Select atleast one Sample to rollback.');
      setActiveTab('sampling');
      return;
    }
    const picked = sampling
      .filter(r => selSamplingIds.has(r.sampleLoginOid))
      .map(r => r.sampleLoginOid);
    if (picked.length === 0) {
      notify.error('Select atleast one Sample to rollback.');
      setActiveTab('sampling');
      return;
    }
    const count = picked.length;
    const reason = await promptDlg({
      title: 'Rollback Reason',
      message: `Enter reason for rolling back ${count} selected sample${count > 1 ? 's' : ''} in job ${selectedJob.jobId}:`,
      required: true,
    });
    if (!reason) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-validation/${selectedJob.jobOid}/rollback`, {
          reason, selectedSampleOids: picked,
        }),
        { blocking: true, label: 'Rolling back…' }
      );
      notify.success('Rolled back to Pending Submission (data intact).');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Rollback failed.';
      notify.error(msg);
    }
  }, [selectedJob, canWriteEff, sampling, selSamplingIds, promptDlg, notify, closeDetail]);

  // ── Island stepper ──────────────────────────────────────────────────────────
  const islandSteps = useMemo((): IslandStep[] => {
    if (view === 'list') {
      const jobDone = selectedIds.size > 0;
      return [
        { id: 'job',        label: 'Select Job', state: jobDone ? 'done'   : 'active' },
        { id: 'stations',   label: 'Stations',   state: 'todo' },
        { id: 'sampling',   label: 'Sampling',   state: 'todo' },
        { id: 'monitoring', label: 'Monitoring', state: 'todo' },
        { id: 'validate',   label: 'Validate',   state: 'todo' },
      ];
    }
    return [
      { id: 'job',        label: 'Select Job', state: 'done' },
      { id: 'stations',   label: 'Stations',   state: visitedTabs.has('stations')   ? 'done' : 'active' },
      { id: 'sampling',   label: 'Sampling',   state: visitedTabs.has('sampling')   ? 'done' : visitedTabs.has('stations')   ? 'active' : 'todo' },
      { id: 'monitoring', label: 'Monitoring', state: visitedTabs.has('monitoring') ? 'done' : visitedTabs.has('sampling')   ? 'active' : 'todo' },
      { id: 'validate',   label: 'Validate',   state: allTabsVisited ? 'active' : 'todo' },
    ];
  }, [view, selectedIds, visitedTabs, allTabsVisited]);

  // sampleLoginOid -> station name (from the Station tab) + trend opener — the
  // Monitoring row has sampleLoginOid but no station name (Routing the Trend Icon).
  const stationNameByLogin = useMemo(
    () => new Map(stations.map((s) => [s.sampleLoginOid, s.stationName])),
    [stations],
  );
  const openTrend = useCallback((siteName: string, parameter: string) => {
    setTrendSite(siteName);
    setTrendParam(parameter);
    setTrendOpen(true);
  }, []);

  // Free-text filter over the list (Job ID / Client / Project / Matrix / Status).
  const filtered = useMemo(() => {
    if (!search.trim()) return listRows;
    const t = search.toLowerCase();
    // Null-safe: a field (projectId/projectName/…) can be null from the API — a bare
    // .toLowerCase() crashed the page to blank on a longer search (user report 2026-09-25).
    const has = (v: string | null | undefined) => (v ?? '').toLowerCase().includes(t);
    return listRows.filter(x =>
      has(x.jobId) || has(x.client) || has(x.projectId) ||
      has(x.projectName) || has(x.sampleMatrix) || has(x.status) || has(x.statusId));
  }, [listRows, search]);

  // ── List grid columns ───────────────────────────────────────────────────────
  const listCols: AppColumn<ValidationListRow>[] = [
    { field: 'jobId',        header: 'JobID',        width: 130 },
    { field: 'client',       header: 'Client',       width: 220 },
    { field: 'projectId',    header: 'ProjectID',    width: 130 },
    { field: 'projectName',  header: 'ProjectName',  width: 230 },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 140 },
    { field: 'status',       header: 'Status',       width: 210,
      render: r => <span className="bl-pill info">{r.status}</span> },
    { field: 'statusId',     header: 'StatusID',     width: 100 },
  ];

  // ── Base (fixed) grid columns, read-only. The Sample Field Configuration is
  //    applied over these via the shared engine (assembleTab/applyFieldConfig),
  //    exactly like Field Data Entry — same captions / visibility / order /
  //    dynamic columns — then stripped of any inline editors (this view is
  //    read-only). ──────────────────────────────────────────────────────────
  const stationBase: AppColumn<StationRow>[] = [
    { field: 'stationId',      header: 'JobID',             width: 120 },
    { field: 'stationName',    header: 'StationLocation',   width: 160 },
    { field: 'blended',        header: 'Blended',           width: 90, align: 'center',
      render: r => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <input type="checkbox" checked={r.blended} readOnly
            style={{ cursor: 'default', width: 14, height: 14 }} />
        </div>
      ) },
    { field: 'latitude',       header: 'Latitude',          width: 120 },
    { field: 'longitude',      header: 'Longitude',         width: 120 },
    { field: 'temperature',    header: 'Temperature (°F)',  width: 140 },
    { field: 'humidity',       header: 'Humidity (%)',      width: 120 },
    { field: 'stationComment', header: 'StationComment',    width: 200 },
  ];
  const samplingBase: AppColumn<SamplingRow>[] = [
    { field: 'stationName',     header: 'StationLocation',  width: 150 },
    { field: 'sampleId',        header: 'SampleID',         width: 140 },
    { field: 'collectorName',   header: 'CollectedBy',      width: 140 },
    { field: 'collectedDate',   header: 'CollectedDate',    width: 160, type: 'datetime',
      render: r => <span>{r.collectedDate ? fmtLocalDateTime(r.collectedDate) : ''}</span> },
    { field: 'testSummary',     header: 'TestSummary',      width: 340 },
    { field: 'sampleComment',   header: 'SampleComment',    width: 180 },
    { field: 'enteredBy',       header: 'EnteredBy',        width: 120 },
    { field: 'enteredDate',     header: 'EnteredDate',      width: 160, type: 'date',
      render: r => <span>{r.enteredDate ? fmtLocalDate(r.enteredDate) : ''}</span> },
  ];
  const monitoringBase: AppColumn<MonitoringRow>[] = [
    { field: 'sampleId',    header: 'SampleID',       width: 140 },
    { field: 'test',        header: 'Test',           width: 180 },
    { field: 'methodName',  header: 'MethodName',     width: 140 },
    { field: 'parameter',   header: 'Parameter',      width: 180 },
    { field: 'result',      header: 'Result',         width: 90, align: 'left' },
    { field: 'unit',        header: 'Units',          width: 110 },
    { field: 'monitoredBy', header: 'MonitoredBy',    width: 130 },
    { field: 'monitoredDate', header: 'MonitoredDate', width: 160, type: 'datetime',
      render: r => <span>{r.monitoredDate ? fmtLocalDateTime(r.monitoredDate) : ''}</span> },
    { field: 'enteredBy',   header: 'EnteredBy',      width: 120 },
    { field: 'enteredDate', header: 'EnteredDate',    width: 160, type: 'date',
      render: r => <span>{r.enteredDate ? fmtLocalDate(r.enteredDate) : ''}</span> },
  ];

  const trendCol: AppColumn<MonitoringRow> = {
    field: 'trend', header: 'Trend', headerText: 'Trend', width: 64, align: 'center',
    sortable: false, filterable: false, noExport: true,
    render: r => {
      const st = stationNameByLogin.get(r.sampleLoginOid) ?? '';
      return <TrendIconButton onClick={() => openTrend(st, r.parameter)}
        disabledReason={st ? undefined : 'No station for this row'} />;
    },
  };

  const stationColSet = useMemo(
    () => { const a = assembleTab(applyFieldConfig(stationBase, fieldConfig, 1, STATION_FIELD_MAP), stationDyn, fieldConfig, 1, STATION_FIELD_MAP);
            return { cols: stripEditable(a.cols), sticky: a.sticky }; },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldConfig, stationDyn]);
  const samplingColSet = useMemo(
    () => { const a = assembleTab(applyFieldConfig(samplingBase, fieldConfig, 0, SAMPLING_FIELD_MAP), samplingDyn, fieldConfig, 0, SAMPLING_FIELD_MAP);
            return { cols: stripEditable(a.cols), sticky: a.sticky }; },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldConfig, samplingDyn]);
  const monitoringColSet = useMemo(
    () => { const a = assembleTab(applyFieldConfig(monitoringBase, fieldConfig, 2, MONITORING_FIELD_MAP), monitoringDyn, fieldConfig, 2, MONITORING_FIELD_MAP);
            // Trend sits immediately AFTER the Units column (user request 2026-09-28,
            // matching Field Data Entry); falls back to last if Units isn't present.
            const cols = stripEditable(a.cols);
            const unitIdx = cols.findIndex(c => c.field === 'unit');
            if (unitIdx >= 0) cols.splice(unitIdx + 1, 0, trendCol);
            else cols.push(trendCol);
            return { cols, sticky: a.sticky }; },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldConfig, monitoringDyn, stationNameByLogin]);

  const stationCols    = stationColSet.cols;
  const samplingCols   = samplingColSet.cols;
  const monitoringCols = monitoringColSet.cols;

  const loadedJob = selectedIds.size === 1
    ? listRows.find(r => selectedIds.has(r.jobOid)) ?? null
    : null;

  // Phase 2.3 — Field Data Validation detail key (CLAUDE.md standing rule 14,
  // §18.6, ADR-0013): Alt+V mirrors the detail-view Validate button (same handler
  // + Write gate).  Only registered in the DETAIL view (empty on the list), so it
  // never fires where there is no Validate button.
  useHotkeys(view === 'list'
    ? [{ combo: 'alt+l', label: 'Load', group: 'Field Data Validation', order: 1,
        run: () => { if (loadedJob) void openDetail(loadedJob); }, disabled: !loadedJob }]
    : [
    { combo: 'alt+v', label: 'Validate', group: 'Field Data Validation', order: 1,
      run: () => { void handleValidate(); }, disabled: !canWriteEff },
    { combo: 'alt+b', label: 'Rollback', group: 'Field Data Validation', order: 2,
      run: () => { void handleRollback(); }, disabled: !canWriteEff || sampling.length === 0 },
    { combo: 'esc', label: 'Back to list', group: 'Field Data Validation', order: 3,
      allowInInput: false, run: () => { closeDetail(); } },
  ]);

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <div className="bl-page">
        <TopbarStepper steps={islandSteps} />

        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Sampling Management</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Field Data Validation</span>
            </div>

            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata-validation" auditFormName="Field Data Validation"
                loadMs={listLoadMs} loading={listLoading} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="ca-btn ca-btn-solid"
                  disabled={!loadedJob}
                  onClick={() => { if (loadedJob) void openDetail(loadedJob); }}
                  title={!loadedJob ? 'Tick a Job ID to load' : `Load ${loadedJob.jobId}`}
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
                {/* Whole-job "Rollback Job" removed from the LIST view (user request
                    2026-09-02). Rollback stays in the DETAIL view, scoped to the ticked
                    samples (GoLive RollBack_Accepting) — click Load, then Rollback. */}
              </div>
            </div>

            {/* ONE standard hint (was a redundant .dp-subtitle + StepHint saying the
                same thing — user request 2026-08-31: clean standalone design). */}
            <StepHint tone="guidance">
              <b>Review · Validate (FD-2-3).</b> Tick a Job ID and click <b>Load</b> (or double-click a row) to review the three tabs, then tick samples to <b>Validate</b> — or <b>Rollback</b> to return them to Pending Submission (data intact).
            </StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              onRefresh={() => void loadList()}
              refreshing={listLoading}
              exportData={{ filename: 'field-data-validation', ...gridExportMatrix(listVisible, listCols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, below the toolbar,
              never inside the toolbar/card (§18.6, Sample Transfer parity). */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>

          {search && filtered.length !== listRows.length && (
            <div style={{ marginBottom: 6 }}>
              <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            </div>
          )}

          <div className="bl-card bl-card-flex">
            {listError ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {listError}
                <button onClick={() => void loadList()}
                  style={{ marginLeft: 10, color: 'var(--brand-accent,#e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Retry
                </button>
              </div>
            ) : (
              <AppDataGrid<ValidationListRow>
                maximizable
                rows={filtered}
                columns={listCols}
                getRowId={r => r.jobOid}
                loading={listLoading}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                onVisibleRowsChange={setListVisible}
                emptyText="Nothing pending validation — submit a job from Field Data Entry."
                /* Double-click (or Enter on the active row) opens that job's detail —
                   the same flow as Tick a Job ID → Load (user request 2026-09-28).
                   Load stays as the primary action. */
                onRowDoubleClick={r => void openDetail(r)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════
  const hintContent = allTabsVisited
    ? (<>All three tabs reviewed — tick the samples on <strong>Sampling Information</strong>, then click <strong>Validate</strong> to move them forward, or <strong>Rollback</strong> to send them back.</>)
    : (<><b>Review · Validate.</b> Read-only review of the submitted field data — visit all three tabs, tick the samples on <strong>Sampling Information</strong>, then <strong>Validate</strong>. Rollback returns the selected samples to the previous queue.</>);

  return (
    <div className="bl-page">
      <TopbarStepper steps={islandSteps} />

      <div className="bl-top">
        <div className="dp-page-hdr">
          {/* Full module path (Home > Sampling Management > … ) matching Field Data
              Entry — user request 2026-08-31. */}
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sampling Management</span>
            <i className="ti ti-chevron-right" />
            <span
              style={{ color: 'var(--brand-accent,#e07b39)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={closeDetail}
            >Field Data Validation</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{selectedJob?.jobId}</span>
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormTitle
              pageId="fielddata-validation"
              title={`Field Data Validation — ${selectedJob?.jobId ?? ''}`}
              auditFormName="Field Data Validation"
              loadMs={detailLoadMs}
              loading={detailLoading}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {/* Back — return to the Field Data Validation list (user request
                  2026-09-28); mirrors the breadcrumb, bound to Esc (rule 14). */}
              <button className="ca-btn" onClick={closeDetail}
                title={`Back to list (${formatCombo('esc')})`}>
                <span aria-hidden="true">←</span>&nbsp;Back
              </button>
              <button
                className="ca-btn ca-btn-solid"
                /* Enabled purely by the MAC Write permission (GoLive parity) — the
                   tab-visit rule was removed 2026-08-03, see handleValidate. */
                disabled={!canWriteEff}
                title={(!canWriteEff
                  ? 'You do not have permission to validate field data'
                  : selSamplingIds.size === 0
                    ? 'Tick the samples to validate on the Sampling Information tab'
                    : `Validate ${selSamplingIds.size} selected sample(s)`) + ` (${formatCombo('alt+v')})`}
                onClick={() => void handleValidate()}
              >
                <span aria-hidden="true">✓</span>&nbsp;Validate
              </button>
              <button
                className="ca-btn"
                disabled={!canWriteEff || sampling.length === 0}
                title={(!canWriteEff
                  ? 'You do not have permission to roll back field data'
                  : sampling.length === 0
                    ? 'No samples to rollback'
                    : selSamplingIds.size === 0
                      // GoLive keeps the action clickable and answers with
                      // "Select atleast one Sample to rollback", so leave it enabled.
                      ? 'Tick the samples to roll back on the Sampling Information tab'
                      : `Roll back ${selSamplingIds.size} selected sample(s)`) + ` (${formatCombo('alt+b')})`}
                onClick={() => void handleRollback()}
              >
                <span aria-hidden="true">↩️</span>&nbsp;Rollback
              </button>
            </div>
          </div>

          {/* Always the orange 'guidance' tone — never turn green when all tabs are
              reviewed (user request 2026-08-31). */}
          <StepHint tone="guidance">
            {hintContent}
          </StepHint>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Read-only notice + toolbar — sits ABOVE the tabs (user request
            2026-09-28: move the tab strip below the Refresh / Export bar). */}
        <div style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AppToolbar
            left={activeTab === 'monitoring' ? (
              <button className="ca-btn" onClick={() => openTrend('', '')}
                title="Concentration trend — pick a site and parameter">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e8670a" strokeWidth="2.4"
                  strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: '-2px' }}>
                  <path d="M3 17l5-5 4 3 7-8" /><path d="M15 4h5v5" /></svg>
                Trending
              </button>
            ) : undefined}
            onRefresh={() => {
              if (selectedJob) void openDetail(selectedJob);
            }}
            refreshing={detailLoading}
            exportData={{
              filename: `field-data-validation-${activeTab}`,
              ...(activeTab === 'stations'  ? gridExportMatrix(stationVisible,    stationCols)
                : activeTab === 'sampling'  ? gridExportMatrix(samplingVisible,   samplingCols)
                :                             gridExportMatrix(monitoringVisible, monitoringCols)),
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 4 }}>Read-only review.</span>
        </div>

        {/* ── Tabs (moved BELOW the toolbar, user request 2026-09-28) ── */}
        <div className="sa-filter-row" style={{ marginBottom: 8 }}>
          {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => {
            const labels: Record<TabKey, string> = {
              stations:   'Station Information',
              sampling:   'Sampling Information',
              monitoring: 'Monitoring Records',
            };
            const visited = visitedTabs.has(tab);
            return (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => switchTab(tab)}
              >
                {labels[tab]}
                {visited && <span style={{ color: '#22c55e', marginLeft: 5, fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Trend popup — the Monitoring "Trend" column + toolbar Trending button */}
        {selectedJob && (
          <TrendChartPopup
            open={trendOpen}
            onClose={() => setTrendOpen(false)}
            siteTrend={{
              siteName: trendSite,
              parameter: trendParam,
              siteOptions: trendSite ? [] : Array.from(new Set(stations.map((s) => s.stationName).filter(Boolean))),
              job: { jobId: selectedJob.jobId, client: selectedJob.client,
                     projectId: selectedJob.projectId, projectName: selectedJob.projectName },
            }}
          />
        )}

        {/* ── Station Information tab ── */}
        {activeTab === 'stations' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<StationRow>
              maximizable
              rows={stations}
              columns={stationCols}
              stickyColumns={stationColSet.sticky}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No station rows."
              selectable
              selectedIds={selStationIds}
              onSelectionChange={setSelStationIds}
              onVisibleRowsChange={setStationVisible}
            />
          </div>
        )}

        {/* ── Sampling Information tab (read-only) — Rollback acts on every row
               shown here (all PendingValidation samples for this job), matching
               GoLive RollBack_Accepting exactly, with no pick step needed ── */}
        {activeTab === 'sampling' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<SamplingRow>
              maximizable
              rows={sampling}
              columns={samplingCols}
              stickyColumns={samplingColSet.sticky}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No sampling rows."
              selectable
              selectedIds={selSamplingIds}
              onSelectionChange={setSelSamplingIds}
              onVisibleRowsChange={setSamplingVisible}
            />
          </div>
        )}

        {/* ── Monitoring Records tab ── */}
        {activeTab === 'monitoring' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<MonitoringRow>
              maximizable
              rows={monitoring}
              columns={monitoringCols}
              stickyColumns={monitoringColSet.sticky}
              getRowId={r => r.sampleParameterOid}
              loading={detailLoading}
              emptyText="No monitoring rows."
              selectable
              selectedIds={selMonitoringIds}
              onSelectionChange={setSelMonitoringIds}
              onVisibleRowsChange={setMonitoringVisible}
            />
          </div>
        )}
      </div>
    </div>
  );
}
